
import { useEffect, useRef } from 'react';

interface AdUnitProps {
  network: 'google-adsense' | 'exoclick' | 'hilltopads';
  format?: 'display' | 'banner' | 'native' | 'video';
  size?: { width: number; height: number };
  className?: string;
}

export default function AdUnit({ 
  network, 
  format = 'display', 
  size = { width: 300, height: 250 },
  className = '' 
}: AdUnitProps) {
  const adContainerRef = useRef<HTMLDivElement>(null);
  const adLoadedRef = useRef(false);

  useEffect(() => {
    if (adLoadedRef.current || !adContainerRef.current) return;

    const loadAd = () => {
      if (!adContainerRef.current) return;

      // Verify container has proper dimensions before loading ad
      const containerRect = adContainerRef.current.getBoundingClientRect();
      if (containerRect.width === 0 || containerRect.height === 0) {
        console.warn('AdUnit container has zero dimensions, retrying...');
        setTimeout(loadAd, 200);
        return;
      }

      if (network === 'google-adsense') {
        // Google AdSense - Real Ad Implementation
        const ins = document.createElement('ins');
        ins.className = 'adsbygoogle';
        ins.style.display = 'block';
        ins.style.width = `${size.width}px`;
        ins.style.height = `${size.height}px`;
        ins.style.minWidth = `${size.width}px`;
        ins.style.minHeight = `${size.height}px`;
        ins.setAttribute('data-ad-client', 'ca-pub-8467771846596141');
        ins.setAttribute('data-ad-slot', '1234567890');
        ins.setAttribute('data-ad-format', 'auto');
        ins.setAttribute('data-full-width-responsive', 'true');

        adContainerRef.current.appendChild(ins);

        // Wait for ins element to be properly rendered
        setTimeout(() => {
          const insRect = ins.getBoundingClientRect();
          if (insRect.width === 0) {
            console.warn('AdSense ins element has zero width, skipping push');
            return;
          }

          // Load AdSense script if not already loaded
          if (!document.querySelector('script[src*="adsbygoogle.js"]')) {
            const script = document.createElement('script');
            script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-8467771846596141';
            script.async = true;
            script.crossOrigin = 'anonymous';
            document.head.appendChild(script);
            
            script.onload = () => {
              // Double check dimensions before pushing
              setTimeout(() => {
                const finalRect = ins.getBoundingClientRect();
                if (finalRect.width > 0) {
                  try {
                    (window.adsbygoogle = window.adsbygoogle || []).push({});
                  } catch (e) {
                    console.error('AdSense error:', e);
                  }
                }
              }, 300);
            };
          } else {
            // Script already loaded, verify dimensions and push
            setTimeout(() => {
              const finalRect = ins.getBoundingClientRect();
              if (finalRect.width > 0) {
                try {
                  (window.adsbygoogle = window.adsbygoogle || []).push({});
                } catch (e) {
                  console.error('AdSense error:', e);
                }
              }
            }, 300);
          }
        }, 100);
      }
    };

    // Ensure DOM is fully ready and container is visible
    const timer = setTimeout(loadAd, 200);
    adLoadedRef.current = true;

    return () => {
      clearTimeout(timer);
      adLoadedRef.current = false;
    };
  }, [network, format, size.width, size.height]);

  return (
    <div 
      ref={adContainerRef}
      className={`ad-unit-container ${className}`}
      style={{
        width: `${size.width}px`,
        height: `${size.height}px`,
        minWidth: `${size.width}px`,
        minHeight: `${size.height}px`,
        display: 'block',
        position: 'relative',
        backgroundColor: '#1a1a1a',
        border: '2px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        overflow: 'hidden'
      }}
    />
  );
}

// TypeScript declarations
declare global {
  interface Window {
    adsbygoogle?: any[];
  }
}
